import { Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";
import FeatureGates from "@atlassian/feature-gate-js-client";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { AiUnionIcon } from "@/templates/confluence-software-page/src/icons/ai-union-icon";

const lozengeStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "3px",
    backgroundColor: "#EAE6FF",
    color: "#403294",
    borderRadius: "3px",
    padding: "0 0.25rem",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.3px",
    lineHeight: "1rem",
    textTransform: "uppercase"
});

const lozengeIcon = css({
    color: "#8777D9"
});

const confluenceHeroSectionStyle = css({
    display: "flex",
    maxWidth: "1200px",
    flexDirection: "column",
    margin: `0 auto ${token("space.600")} auto`,
    alignItems: "center",
    paddingBlockStart: token("space.800")
});

const heroSectionTextStyle = css({
    "flex": 1,
    "display": "flex",
    "flexDirection": "column",
    "alignItems": "center",
    "textAlign": "center",
    "padding": `${token("space.150")} ${token("space.200")} 0 ${token(
        "space.200"
    )}`,
    "maxWidth": "768px",
    ".confluenceHero h1": {
        "color": "#253858",
        "fontWeight": 500,
        "margin": `${token("space.0")} ${token("space.0")} ${token(
            "space.200"
        )} ${token("space.0")}`,
        "textAlign": "center",
        "@media (max-width: 47.99rem)": {
            fontSize: "2.5rem",
            lineHeight: "2.875rem"
        },
        "@media (min-width: 48rem) and (max-width: 63.99rem)": {
            fontSize: "2.75rem",
            lineHeight: "3.125rem"
        },
        "@media (min-width: 64rem)": {
            fontSize: "3rem",
            lineHeight: "3.5rem"
        }
    },
    ".confluenceHero p": {
        "margin": `${token("space.0")} ${token("space.0")} ${token(
            "space.300"
        )} ${token("space.0")}`,
        "textAlign": "center",
        "fontWeight": 400,
        "color": "#091E42",
        "@media (max-width: 63.99rem)": {
            fontSize: "1.125rem",
            lineHeight: "1.625rem"
        },
        "@media (min-width: 64rem)": {
            fontSize: "1.25rem",
            lineHeight: "1.75rem"
        }
    },
    ".confluenceHero .contentBlock-linkFlex": {
        gap: "16px",
        justifyContent: "center",
        marginBlockEnd: 0
    },
    ".confluenceHero .contentBlock-linkFlex a": {
        marginBlockStart: 0
    },
    ".confluenceHero .link-wrapper": {
        "@media (max-width: 63.99rem)": {
            display: "none"
        }
    }
});

const HeroSection = () => {
    const intl = useIntl();
    const getItFreeText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.heroCTANew",
        defaultMessage: "Get Confluence free"
    });
    const tryDemoText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.tryDemo",
        defaultMessage: "Try a demo"
    });
    const showSetKnwoledgeFreeHeader = FeatureGates.getExperimentValue(
        "cfind-1463-set-knowledge-free",
        "enrolled",
        false
    );
    return (
        <Fragment>
            <section>
                <div css={confluenceHeroSectionStyle}>
                    <div css={lozengeStyle}>
                        <span css={lozengeIcon}>
                            <AiUnionIcon size="9px" />
                        </span>
                        <FormattedMessage
                            id="ConfluenceSoftwarePageTemplate.heroLozenge"
                            defaultMessage="powered by ai"
                        />
                    </div>
                    <div css={heroSectionTextStyle}>
                        <ContentBlock
                            headerType="h1"
                            className={"confluenceHero"}
                            headerText={
                                showSetKnwoledgeFreeHeader
                                    ? intl.formatMessage({
                                          id: "ConfluenceSoftwarePageTemplate.heroHeaderSetKnowledgeFree",
                                          defaultMessage: "Set knowledge free"
                                      })
                                    : intl.formatMessage({
                                          id: "ConfluenceSoftwarePageTemplate.heroHeaderNew",
                                          defaultMessage:
                                              "Goodbye silos, hello teamwork"
                                      })
                            }
                            bodyText={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.heroBodyNew",
                                defaultMessage:
                                    "Create, organize, and share work with AI by your side. Turn scattered information into a single source of truth."
                            })}
                            ctaProps={{
                                buttonProps: {
                                    text: getItFreeText,
                                    color: "darkblue",
                                    url: useLocalizedHref(
                                        "/software/confluence/free"
                                    ),
                                    className: "confluence-hero-section-cta",
                                    dataEventProps: {
                                        "data-product-key": "confluence",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-event": "clicked",
                                        "data-label": getItFreeText,
                                        "data-track-event":
                                            "heroSection.getItFree"
                                    }
                                },
                                secondaryLinkProps: {
                                    text: tryDemoText,
                                    href: "/confluence-sandbox-editor",
                                    type: "basic-underline",
                                    className: "confluence-hero-section-editor",
                                    doesArrowAnimate: true,
                                    dataEventProps: {
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-event": "clicked",
                                        "data-product-key": "confluence",
                                        "data-label-english": "Try a demo",
                                        "data-label": tryDemoText,
                                        "data-track-event":
                                            "heroSection.tryADemo"
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default HeroSection;
